import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import type { RootState } from "../../../common/state/store";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { IAuthFormData } from "../state/authSlice";
import { clearMfaChallenge, setAuth, signIn } from "../state/authSlice";
import { closeEventSource } from "../../../common/util/EventSource";
import { useNavigate } from "react-router-dom";
import { jwtSelector, mfaChallengeSelector } from "../../../common/state/selectors/authSelector";
import React from "react";
import { setError } from "../../../common/state/slice/appStateSlice";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { useError } from "../../../common/components/AppState/Error";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/outline";
import MfaChallengeForm from "./MfaChallengeForm";
import MfaEnrollDialog from "../../mfa/components/MfaEnrollDialog";
import RecoveryCodesDialog from "../../mfa/components/RecoveryCodesDialog";
import { clearEnrollmentWizardState } from "../../mfa/state/mfaSlice";
import type { IMfaEnrollConfirmResponse } from "../../mfa/state/mfaSlice";
import { setProjectGroupsWithProjects } from "../../projectGroups/state/projectGroupsSlice";
import { storeAuthInLocalStorage } from "../../../common/util/localStorage";
import wixarLogo from "src/assets/wixar-logo-tagline.png";
import NetworkBackground from "./NetworkBackground";

export const LOGIN_COMPONENT_ID = "loginComponent";

interface ILoginForm {
  labels: {
    title: string;
    subtitle: string;
    email: string;
    password: string;
    lostpwd: string;
    login: string;
  };
  onSubmit: (data: IAuthFormData) => void;
  isSubmitting: boolean;
}

export const LoginFormContainer = ({ mobile }: { mobile: boolean }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [enrollLoginResult, setEnrollLoginResult] = useState<IMfaEnrollConfirmResponse | null>(
    null,
  );
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);

  const { jwt, mfaChallenge } = useAppSelector((state: RootState) => {
    return {
      jwt: jwtSelector(state),
      mfaChallenge: mfaChallengeSelector(state),
    };
  });

  const labels = {
    title: t("pages.login.title"),
    subtitle: t("pages.login.subtitle"),
    email: t("pages.login.workEmail"),
    password: t("general.password"),
    lostpwd: t("pages.login.lostpwd"),
    login: t("pages.login.login"),
  };

  const onSubmit = (data: IAuthFormData) => {
    setIsSubmitting(true);
    closeEventSource();
    const from_environment = mobile ? "WebAppMobile" : "WebApp";
    dispatch(signIn({ componentId: LOGIN_COMPONENT_ID, from_environment, ...data }))
      .unwrap()
      .then(() => {
        setIsSubmitting(false);
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const handleBackToCredentials = () => {
    dispatch(clearMfaChallenge());
    dispatch(clearEnrollmentWizardState());
    dispatch(setError({ componentId: LOGIN_COMPONENT_ID, error: "" }));
  };

  const handleEnrollConfirmed = (result?: IMfaEnrollConfirmResponse) => {
    setEnrollLoginResult(result && result.jwt ? result : null);
    setShowRecoveryCodes(true);
  };

  const handleRecoveryCodesAcknowledged = () => {
    dispatch(clearEnrollmentWizardState());

    const result = enrollLoginResult;
    if (!result || !result.jwt || !result.user) {
      setEnrollLoginResult(null);
      setShowRecoveryCodes(false);
      return;
    }

    const loginJwt = result.jwt;
    const helpDocsJwt = result.helpDocsJwt;
    const user = result.user;
    const firstTwoProjectGroupsWithProjects = result.firstTwoProjectGroupsWithProjects;
    const organization = user.organization;

    if (
      Array.isArray(firstTwoProjectGroupsWithProjects) &&
      firstTwoProjectGroupsWithProjects.length > 0
    ) {
      dispatch(setProjectGroupsWithProjects(firstTwoProjectGroupsWithProjects));
    }

    void storeAuthInLocalStorage(user, organization, loginJwt, helpDocsJwt ?? "", true).then(() => {
      dispatch(setAuth({ jwt: loginJwt, user, organization }));
      dispatch(clearMfaChallenge());
      setEnrollLoginResult(null);
      setShowRecoveryCodes(false);
    });
  };

  React.useEffect(() => {
    if (jwt && jwt !== "") {
      navigate("/");
    }
  }, [jwt]);

  if (mfaChallenge?.requirement === "mfa_required") {
    return (
      <LoginPageLayout>
        <MfaChallengeForm
          mfaToken={mfaChallenge.token}
          method={mfaChallenge.method}
          sentTo={mfaChallenge.sentTo}
          onBackToCredentials={handleBackToCredentials}
        />
      </LoginPageLayout>
    );
  }

  if (mfaChallenge?.requirement === "mfa_enrollment_required") {
    return (
      <>
        <LoginPageLayout>
          <MfaEnrollDialog
            isOpen={!showRecoveryCodes}
            isReplacement={false}
            variant="inline"
            mfaToken={mfaChallenge.token}
            onClose={handleBackToCredentials}
            onEnrolled={handleEnrollConfirmed}
          />
        </LoginPageLayout>
        <RecoveryCodesDialog isOpen={showRecoveryCodes} onClose={handleRecoveryCodesAcknowledged} />
      </>
    );
  }

  return (
    <LoginPageLayout>
      <LoginForm labels={labels} onSubmit={onSubmit} isSubmitting={isSubmitting} />
    </LoginPageLayout>
  );
};

const PITCH_FIGURES = ["reach", "library", "data"] as const;

const LoginPitch = () => {
  const { t } = useTranslation();

  return (
    <div>
      <div className="h-1 w-14 rounded-sm bg-green-500" />
      <p className="mt-6 max-w-[520px] text-[clamp(26px,3.1vw,44px)] font-extrabold leading-[1.1] tracking-tight">
        {t("pages.login.pitch.claim")}
      </p>
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:mt-12">
        {PITCH_FIGURES.map((figure) => (
          <div key={figure} className="rounded-2xl border border-green-200 bg-white p-5">
            <b className="block text-[13px] font-bold text-green-800">
              {t(`pages.login.pitch.${figure}Label`)}
            </b>
            <strong className="mt-1 block whitespace-nowrap text-[clamp(22px,2.1vw,30px)] font-extrabold">
              {t(`pages.login.pitch.${figure}Value`)}
            </strong>
            <span className="mt-1 block text-sm text-blue-400">
              {t(`pages.login.pitch.${figure}Caption`)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LoginLinks = () => {
  const { t } = useTranslation();
  const links = [
    { key: "privacy", href: "https://www.wixar.io/fr/politiques-de-confidentialite" },
    { key: "legal", href: "https://www.wixar.io/fr/mentions-legales" },
    { key: "help", href: "mailto:contact@wixar.io" },
  ];

  return (
    <nav className="flex flex-wrap items-center justify-center gap-2.5 text-[13px] text-blue-400">
      {links.map(({ key, href }, index) => (
        <React.Fragment key={key}>
          {index > 0 && <span aria-hidden="true">·</span>}
          <a href={href} className="hover:text-blue-500">
            {t(`pages.login.links.${key}`)}
          </a>
        </React.Fragment>
      ))}
    </nav>
  );
};

export const LoginPageLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative isolate min-h-screen overflow-hidden bg-slate-50 font-Hanken text-blue-500 antialiased lg:grid lg:grid-cols-2">
      <NetworkBackground />
      <aside className="hidden flex-col border-r border-green-200 bg-green-500/[.07] px-[clamp(32px,5vw,72px)] py-14 lg:flex">
        <img src={wixarLogo} alt="wixar-logo" className="block h-auto w-[330px] max-w-full" />
        <div className="grow" />
        <LoginPitch />
        <div className="grow" />
      </aside>
      <main className="flex min-h-screen flex-col bg-slate-50/50 px-4 pb-6 pt-5 sm:px-[clamp(24px,5vw,72px)] sm:py-10">
        <img
          src={wixarLogo}
          alt="wixar-logo"
          className="block h-auto w-[140px] sm:w-[170px] lg:hidden"
        />
        <div className="flex grow items-center justify-center py-8">
          <div className="w-full max-w-[440px] rounded-[20px] border border-gray-200 bg-white px-[22px] py-7 shadow-[0_18px_48px_rgba(12,30,89,0.08)] sm:rounded-3xl sm:px-10 sm:pb-9 sm:pt-10">
            {children}
          </div>
        </div>
        <div className="mx-auto mb-10 w-full max-w-[440px] sm:max-w-[640px] lg:hidden">
          <LoginPitch />
        </div>
        <LoginLinks />
      </main>
    </div>
  );
};

const LoginForm = ({ labels, onSubmit, isSubmitting }: ILoginForm) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [missingCredentials, setMissingCredentials] = useState(false);
  const { register, handleSubmit } = useForm<IAuthFormData>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginError = useError(LOGIN_COMPONENT_ID);

  const submit = handleSubmit(
    (data) => {
      setMissingCredentials(false);
      onSubmit(data);
    },
    () => setMissingCredentials(true),
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !isSubmitting) {
      event.preventDefault();
      void submit();
    }
  };

  const errorMessage = missingCredentials
    ? t("pages.login.missingCredentials")
    : loginError
    ? t("pages.login.invalidCredentials")
    : "";

  const inputClassName =
    "h-[52px] rounded-xl border-gray-300 px-4 text-base font-medium text-blue-500 shadow-none placeholder:text-blue-300 focus:border-green-500 focus:ring-4 focus:ring-green-500/20";

  return (
    <form noValidate onSubmit={(e) => void submit(e)} className="flex flex-col gap-[22px]">
      <div>
        <h1 className="text-[28px] font-extrabold leading-[1.1] tracking-tight sm:text-[34px]">
          {labels.title}
        </h1>
        <p className="mt-2 text-base leading-snug text-blue-400">{labels.subtitle}</p>
      </div>
      {errorMessage && (
        <p
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm font-semibold text-red-700"
        >
          {errorMessage}
        </p>
      )}
      <IsLoading componentId={LOGIN_COMPONENT_ID} showSuccess={false} />
      <div className="flex flex-col gap-2">
        <label htmlFor="login-email" className="text-sm font-semibold">
          {labels.email}
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="username"
          placeholder={t("pages.login.workEmailPlaceholder")}
          className={inputClassName}
          onKeyDown={handleKeyDown}
          {...register("email", { required: true, pattern: /^\S+@\S+$/i })}
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor="login-password" className="text-sm font-semibold">
            {labels.password}
          </label>
          <button
            type="button"
            className="text-sm font-semibold text-green-800 hover:text-green-900"
            onClick={() => navigate("/forgottenpassword")}
          >
            {labels.lostpwd}
          </button>
        </div>
        <div className="relative">
          <input
            id="login-password"
            type={passwordVisible ? "text" : "password"}
            autoComplete="current-password"
            placeholder={t("pages.login.passwordPlaceholder")}
            className={`${inputClassName} pr-[52px]`}
            onKeyDown={handleKeyDown}
            {...register("password", { required: true })}
          />
          <button
            type="button"
            aria-pressed={passwordVisible}
            aria-label={t(
              passwordVisible ? "pages.login.hidePassword" : "pages.login.showPassword",
            )}
            onClick={() => setPasswordVisible(!passwordVisible)}
            className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-[10px] text-blue-400"
          >
            {passwordVisible ? <EyeOffIcon className="h-6 w-6" /> : <EyeIcon className="h-6 w-6" />}
          </button>
        </div>
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        className="mt-1 h-[54px] rounded-full bg-green-500 text-base font-bold text-blue-500 transition hover:-translate-y-px hover:bg-green-400 disabled:translate-y-0 disabled:cursor-wait disabled:opacity-60"
      >
        {labels.login}
      </button>
      <p className="text-center text-sm text-blue-400">
        {t("pages.login.notCustomer")}{" "}
        <a
          href={t("pages.login.demoUrl")}
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-green-800 hover:text-green-900"
        >
          {t("pages.login.register-beta")}
        </a>
      </p>
    </form>
  );
};
